import type { FloatingPosition, MantineColor } from "@mantine/core";
import { Box, Popover, rem, Stack, Text } from "@mantine/core";
import React, { useState } from "react";

interface AppTooltipProps {
  labels: string[];
  icon?: React.ReactNode;
  children: React.ReactNode;
  color?: string;
  textColor?: MantineColor;
  position?: FloatingPosition;
  crossAxis?: number;
}

export const AppTooltip: React.FC<AppTooltipProps> = ({
  labels,
  icon,
  children,
  color = "#F56565",
  textColor = "black",
  position = "top",
  crossAxis = 0,
}) => {
  const [opened, setOpened] = useState(false);

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      position={position}
      withArrow
      withinPortal
      disabled={labels.length === 0}
      shadow="md"
      trapFocus={false}
      offset={{ mainAxis: 8, crossAxis: crossAxis }}
      middlewares={{
        flip: true,
        shift: true,
      }}
      arrowSize={20}
      styles={{
        dropdown: {
          backgroundColor: color,
          borderRadius: rem(16),
          padding: rem(14),
          color: textColor,
          maxWidth: rem(400),
          wordBreak: "break-word",
          whiteSpace: "normal",
          overflowWrap: "anywhere",
          zIndex: 99,
          "@media (maxWidth: 768px)": {
            maxWidth: rem(120),
          },
          "@media (maxWidth: 480px)": {
            maxWidth: rem(280),
          },
        },
        arrow: {
          width: rem(16),
          height: rem(10),
          backgroundColor: color,
          color: textColor,
        },
      }}
    >
      <Box
        style={{
          position: "relative",
          display: "inline-block",
          width: position === "bottom" ? "fit-content" : "100%",
        }}
      >
        <Popover.Target>
          <div
            onMouseEnter={() => setOpened(true)}
            onMouseLeave={() => setOpened(false)}
            style={{
              cursor: "pointer",
              width: position === "right" ? "fit-content" : "100%",
              display: "inline-block",
            }}
          >
            {children}
          </div>
        </Popover.Target>
      </Box>

      <Popover.Dropdown>
        <Box
          style={{
            display: "flex",
            gap: rem(10),
            alignItems: "flex-start",
          }}
        >
          {icon && (
            <Box
              style={{
                backgroundColor: "#fff",
                borderRadius: "50%",
                width: rem(28),
                height: rem(28),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {icon}
            </Box>
          )}
          <Stack gap="0.25rem" style={{ flex: 1 }}>
            {labels.map((label, idx) => (
              <Text
                key={idx}
                style={{
                  fontSize: rem(16),
                  fontWeight: 600,
                  color: textColor,
                  lineHeight: 1.4,
                }}
              >
                {label}
              </Text>
            ))}
          </Stack>
        </Box>
      </Popover.Dropdown>
    </Popover>
  );
};
